import React, { useState } from 'react';
import { BPForm } from '@common/BPForm';
import { AuthField } from '../Common/AuthField';
import { Layout } from './Layout';
import { Button, Divider, Header, Message, Segment } from 'semantic-ui-react';
import * as yup from 'yup';
import { userManageClient } from '../userManageClient';
import { ChangePasswordRequest } from '@app/api2/api';
import { useTranslation2 } from '../../Contexts/TranslationContext2';
import { BPButton } from '@/components/bp/bpbutton';

interface ChangePasswordFormValues {
    oldPassword: string;
    newPassword: string;
    confirmPassword: string;
}

export const ChangePassword: React.FC = () => {
    const { translations: t } = useTranslation2();
    const [changeError, setChangeError] = useState<string>('');
    const [successMessage, setSuccessMessage] = useState<string>('');

    const initialValues: ChangePasswordFormValues = {
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
    };

    const validationSchema: yup.SchemaOf<ChangePasswordFormValues> = yup.object().shape({
        oldPassword: yup.string()
            .required('Current password is required'),
        newPassword: yup.string()
            .min(6, 'The password must be at least 6 characters long')
            .max(100, 'The password must be at most 100 characters long')
            .required('New password is required'),
        confirmPassword: yup.string()
            .oneOf([yup.ref('newPassword')], 'The new password and confirmation password do not match')
            .required('Confirm password is required')
    });

    const handleSubmit = async (values: ChangePasswordFormValues, { resetForm }: { resetForm: () => void }) => {
        setChangeError('');
        setSuccessMessage('');
        
        try {
            const request: ChangePasswordRequest = {
                oldPassword: values.oldPassword,
                newPassword: values.newPassword,
                confirmPassword: values.confirmPassword
            };

            const result = await userManageClient.changePassword(request);

            if (result.success) {
                setSuccessMessage('Your password has been changed.');
                resetForm();
            } else {
                setChangeError(result.errors?.join(', ') || 'Failed to change password.');
            }
        } catch (error) {
            console.error('Change password error:', error);
            setChangeError('An error occurred while changing your password. Please try again.');
        }
    };

    return (
        <Layout>
            <Header as="h3">{t.account.changePassword}</Header>
            {/* <p>{t.account.changePassword}</p> */}

            <Segment>
                {successMessage && (
                    <Message success>
                        {successMessage}
                    </Message>
                )}

                {changeError && (
                    <Message error>
                        {changeError}
                    </Message>
                )}

                <BPForm
                    initialValues={initialValues}
                    vaildationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    hideButtons={true}
                >
                    {(formProps) => (
                        <>
                            <AuthField
                                name="oldPassword"
                                label={t.account.currentPassword}
                                type="password"
                                autoComplete="current-password"
                                placeholder={t.account.currentPassword}
                            />

                            <AuthField
                                name="newPassword"
                                label={t.account.newPassword}
                                type="password"
                                autoComplete="new-password"
                                placeholder={t.account.newPassword}
                            />
                            
                            <AuthField
                                name="confirmPassword"
                                label={t.account.confirmNewPassword}
                                type="password"
                                autoComplete="new-password"
                                placeholder={t.account.confirmNewPassword}
                            />
                            
                            <Divider />
                            
                            <BPButton 
                                type="submit" 
                                primary 
                                size="large" 
                                disabled={formProps.isSubmitting}
                                loading={formProps.isSubmitting}
                            >
                                {t.account.changePassword}
                            </BPButton>
                        </>
                    )}
                </BPForm>
            </Segment>
        </Layout>
    );
};